import { Box, Tooltip, Typography } from "@mui/material"
import { extraColors } from "../../utils/theme"
import { V2_ACCENT, V2_FONT_MONO, V2_NUMERIC } from "./v2/biothreatV2Tokens"
import type { TrendKind, TrendResult } from "./gdeltClusterAnalytics"

const TREND_STYLE: Record<
  Exclude<TrendKind, "insufficient">,
  { label: string; glyph: string; color: string }
> = {
  accelerating: { label: "Accelerating", glyph: "▲", color: V2_ACCENT.gold },
  linear: { label: "Steady", glyph: "—", color: V2_ACCENT.cyan },
  decelerating: { label: "Decelerating", glyph: "▼", color: V2_ACCENT.green },
}

/** Significance of the rate change, formatted as a confidence cue. */
function formatP(pValue: number): string {
  if (pValue < 0.001) return "p<0.001"
  if (pValue < 0.01) return "p<0.01"
  return `p=${pValue.toFixed(2)}`
}

/** Plain-language directional expectation for the current trend. */
const DIRECTION_BLURB: Record<Exclude<TrendKind, "insufficient">, string> = {
  accelerating:
    "Reporting is growing — articles are arriving faster than before.",
  decelerating:
    "Reporting is declining — articles are arriving slower than before.",
  linear: "Reporting is steady — no clear growth or decline.",
}

/** Reads the p-value into how sure we are the DIRECTION is real (not noise). */
function confidencePhrase(pValue: number): string {
  if (pValue < 0.001) return "near-certain this direction is real, not noise"
  if (pValue < 0.01) return "strong evidence this direction is real"
  if (pValue < 0.05) return "some evidence this direction is real"
  return "too weak to be sure — likely just noise"
}

/**
 * Reads R² into how reliable the SHAPE is, named for whatever pace number the
 * card actually shows (doubling-time when rising, decline-rate when falling).
 * Covers every fit tier so there's no special-cased state.
 */
function fitPhrase(trend: TrendResult): string {
  const tier =
    trend.rSquared >= 0.7
      ? "clean"
      : trend.rSquared >= 0.3
        ? "choppy"
        : "erratic"
  if (trend.kind === "linear") {
    return `day-to-day counts are ${tier}; with no clear trend this is mostly noise`
  }
  const pace = trend.kind === "accelerating" ? "doubling-time" : "decline-rate"
  if (tier === "clean")
    return `counts track the trend cleanly, so the ${pace} estimate is reliable`
  if (tier === "choppy")
    return `counts are choppy, so treat the ${pace} estimate as rough`
  return `counts are erratic, so trust the direction, not the exact ${pace}`
}

/**
 * Tooltip body for the trend chip. Reads the two numbers ON the card —
 * direction comes from comparing recent vs. earlier TOTALS (the p-value); the
 * pace comes from fitting a curve to DAY-BY-DAY counts (R²). They answer
 * different questions, so e.g. p<0.001 with a low R² means the move is real but
 * bumpy. Both lines are always shown and adapt to every value.
 */
function TrendExplainer({ trend }: { trend: TrendResult }) {
  const kind = trend.kind === "insufficient" ? "linear" : trend.kind
  return (
    <Box sx={{ p: 0.5, maxWidth: 250 }}>
      <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}>
        {DIRECTION_BLURB[kind]}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4, mt: 1 }}>
        <strong>{formatP(trend.pValue)}</strong> —{" "}
        {confidencePhrase(trend.pValue)}.
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4, mt: 1 }}>
        <strong>R² {trend.rSquared.toFixed(2)}</strong> — {fitPhrase(trend)}.
      </Typography>
    </Box>
  )
}

function trendDetail(trend: TrendResult): string {
  const ratio = trend.recentVsEarlyRatio
  const ratioText = Number.isFinite(ratio) ? `×${ratio.toFixed(1)}` : "×∞"
  if (trend.kind === "accelerating") {
    const doubling =
      trend.doublingDays != null
        ? `doubling ~${Math.max(1, Math.round(trend.doublingDays))}d`
        : "rising fast"
    return `${doubling} · ${ratioText} vs. earlier`
  }
  if (trend.kind === "decelerating") {
    return `${ratioText} vs. earlier`
  }
  return `inflow ${ratioText} vs. earlier`
}

/** Trend chip + supporting metrics for a cluster's article inflow. */
export function GdeltClusterTrendInsight({ trend }: { trend: TrendResult }) {
  if (trend.kind === "insufficient") {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${extraColors.translucent5}`,
        }}
      >
        <SectionEyebrow text="Inflow trend" />
        <Typography
          variant="caption"
          sx={{ color: extraColors.hint, display: "block", mt: 0.5 }}
        >
          Not enough reporting yet to assess a trend.
        </Typography>
      </Box>
    )
  }

  const style = TREND_STYLE[trend.kind]
  return (
    <Tooltip arrow placement="top" title={<TrendExplainer trend={trend} />}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${style.color}`,
          backgroundColor: "rgba(255,255,255,0.03)",
        }}
      >
        <SectionEyebrow text="Inflow trend" />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
          <Typography sx={{ color: style.color, fontSize: "0.9rem" }}>
            {style.glyph}
          </Typography>
          <Typography
            sx={{
              ...V2_NUMERIC,
              color: style.color,
              fontSize: "1rem",
              letterSpacing: "0.02em",
            }}
          >
            {style.label}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: V2_FONT_MONO,
            fontSize: "0.65rem",
            color: extraColors.translucent50,
            mt: 0.25,
          }}
        >
          {trendDetail(trend)}
        </Typography>
        <Typography
          sx={{
            fontFamily: V2_FONT_MONO,
            fontSize: "0.6rem",
            color: extraColors.translucent50,
            mt: 0.25,
          }}
        >
          {formatP(trend.pValue)} · R² {trend.rSquared.toFixed(2)}
        </Typography>
      </Box>
    </Tooltip>
  )
}

function SectionEyebrow({ text }: { text: string }) {
  return (
    <Typography
      sx={{
        fontFamily: V2_FONT_MONO,
        fontSize: "0.6rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: extraColors.translucent50,
      }}
    >
      {text}
    </Typography>
  )
}
